import type { FC } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFetch } from 'loti-request';

import { getApiUrl } from 'app/utils/api';
import type { SelectOption, SelectAction } from 'app/components/SelectDropdown';
import SelectDropdown from 'app/components/SelectDropdown';
import UserAvatar from 'app/components/UserAvatar';

import style from './style.scss';

interface Props {
  value: number | undefined;
  alignment?: Alignment;
  onChange?: (value: number) => void;
  onUnassign?: () => void;
  disabled?: boolean;
  className?: string;
}

interface TraderOptionProps {
  user: { id: number; name: string; image_url: string | null };
}

const TraderOption: FC<TraderOptionProps> = ({ user }) => (
  <span className={style.option}>
    <UserAvatar size={14} className={style.avatar} user={user} />
    <span>{user.name}</span>
  </span>
);

const TraderSelector: FC<Props> = ({
  value,
  alignment,
  onChange,
  onUnassign,
  disabled,
  className
}) => {
  const [t] = useTranslation();
  const { data, withLoader } = useFetch<User[]>({ url: getApiUrl('/users/traders') });
  const traders = data ?? [];

  const options: SelectOption<number>[] = traders.map(trader => ({
    label: <TraderOption user={trader} />,
    value: trader.id
  }));

  const actions: SelectAction[] = [];
  if (onUnassign && value) {
    actions.push({
      key: 'unassigned',
      label: <span className={style.unassign}>{t('events-delete_assignment')}</span>,
      onClick: onUnassign
    });
  }

  return (
    <SelectDropdown
      label={<TraderOption user={{ id: -1, name: t('events-not_assigned'), image_url: null }} />}
      options={options}
      value={value ?? -1}
      disabled={disabled}
      variation="empty"
      alignment={alignment}
      loading={withLoader}
      className={classnames(style.select, className)}
      onChange={onChange}
      actions={actions}
    />
  );
};

TraderSelector.displayName = 'TraderSelector';

export default TraderSelector;
